import { attachmentsService } from '@/services/api/attachmentsService';
import { obstructionAttachmentsService } from '@/services/api/obstructionAttachmentsService';
import { notesService } from '@/services/api/notesService';
import { publicShareService } from '@/services/api/publicShareService';
import type { Attachment } from '@/types';
import type { NoteAttachment } from '@/types/notes';
import type { ObstructionAttachment } from '@/types/obstructions';

export type AttachmentSource =
  | 'task'
  | 'note'
  | 'obstruction'
  | 'public'
  | 'publicNote';

export interface PreviewableAttachment {
  id: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  source: AttachmentSource;
  /** Required when `source` is `'public'` / `'publicNote'`; selects the public share token. */
  shareToken?: string;
  /** Upload timestamp — surfaced by read-only viewers. Optional for sources that omit it. */
  uploadedAt?: string | Date;
}

export function taskAttachmentsToPreviewable(
  attachments: readonly Attachment[],
  shareToken?: string
): PreviewableAttachment[] {
  const source: AttachmentSource = shareToken ? 'public' : 'task';
  return attachments.map((attachment) => ({
    id: attachment.id,
    fileName: attachment.name,
    contentType: attachment.type,
    fileSize: attachment.size,
    source,
    shareToken,
    uploadedAt: attachment.uploadedAt,
  }));
}

export function obstructionAttachmentsToPreviewable(
  attachments: readonly ObstructionAttachment[]
): PreviewableAttachment[] {
  return attachments.map((attachment) => ({
    id: attachment.id,
    fileName: attachment.name,
    contentType: attachment.type,
    fileSize: attachment.size,
    source: 'obstruction',
    uploadedAt: attachment.uploadedAt,
  }));
}

export function noteAttachmentsToPreviewable(
  attachments: readonly NoteAttachment[],
  shareToken?: string
): PreviewableAttachment[] {
  const source: AttachmentSource = shareToken ? 'publicNote' : 'note';
  return attachments.map((attachment) => ({
    id: attachment.id,
    fileName: attachment.fileName,
    contentType: attachment.contentType,
    fileSize: attachment.fileSize,
    source,
    shareToken,
  }));
}

/** Minimal addressing info needed to resolve an attachment's endpoints. */
type AttachmentRef = Pick<
  PreviewableAttachment,
  'id' | 'source' | 'shareToken'
>;

interface AttachmentEndpoints {
  fetchPreview: (ref: AttachmentRef) => Promise<{ data: ArrayBuffer }>;
  getDownloadUrl: (ref: AttachmentRef) => string;
}

/** Guards that a public attachment carries the share token it needs. */
function requireShareToken(shareToken?: string): string {
  if (!shareToken) {
    throw new Error('Public attachment requires a share token');
  }
  return shareToken;
}

const endpointsBySource: Record<AttachmentSource, AttachmentEndpoints> = {
  task: {
    fetchPreview: (ref) => attachmentsService.fetchPreview(ref.id),
    getDownloadUrl: (ref) => attachmentsService.getDownloadUrl(ref.id),
  },
  note: {
    fetchPreview: (ref) => notesService.fetchAttachmentPreview(ref.id),
    getDownloadUrl: (ref) => notesService.getAttachmentDownloadUrl(ref.id),
  },
  obstruction: {
    fetchPreview: (ref) => obstructionAttachmentsService.fetchPreview(ref.id),
    getDownloadUrl: (ref) =>
      obstructionAttachmentsService.getDownloadUrl(ref.id),
  },
  public: {
    fetchPreview: (ref) =>
      publicShareService.fetchAttachmentPreview(
        requireShareToken(ref.shareToken),
        ref.id
      ),
    getDownloadUrl: (ref) =>
      publicShareService.getAttachmentDownloadUrl(
        requireShareToken(ref.shareToken),
        ref.id
      ),
  },
  publicNote: {
    fetchPreview: (ref) =>
      publicShareService.fetchNoteAttachmentPreview(
        requireShareToken(ref.shareToken),
        ref.id
      ),
    getDownloadUrl: (ref) =>
      publicShareService.getNoteAttachmentDownloadUrl(
        requireShareToken(ref.shareToken),
        ref.id
      ),
  },
};

export async function fetchAttachmentData(
  attachment: Pick<PreviewableAttachment, 'id' | 'source' | 'shareToken'>
): Promise<ArrayBuffer> {
  const response =
    await endpointsBySource[attachment.source].fetchPreview(attachment);
  return response.data;
}

export function triggerBlobDownload(
  data: ArrayBuffer,
  fileName: string,
  contentType: string
): void {
  const blob = new Blob([data], { type: contentType });
  const url = URL.createObjectURL(blob);
  triggerLinkDownload(url, fileName);
  URL.revokeObjectURL(url);
}

function triggerLinkDownload(href: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Saves the attachment to the user's disk.
 *
 * Without preloaded bytes we click a hidden `<a href download>` pointing at the
 * same-origin download endpoint. The browser follows the 302 to the storage signed
 * URL natively (no XHR / no CORS) and the response's `Content-Disposition: attachment`
 * triggers a download — picking `<a>` over `window.location.href` keeps the SPA
 * history stack clean.
 */
export async function downloadAndSaveAttachment(
  attachment: Pick<
    PreviewableAttachment,
    'id' | 'fileName' | 'contentType' | 'source' | 'shareToken'
  >,
  preloadedData?: ArrayBuffer | null
): Promise<void> {
  if (preloadedData) {
    triggerBlobDownload(
      preloadedData,
      attachment.fileName,
      attachment.contentType
    );
    return;
  }
  const downloadUrl =
    endpointsBySource[attachment.source].getDownloadUrl(attachment);
  triggerLinkDownload(downloadUrl, attachment.fileName);
}
